/**
 * UserMenu — right-side header affordance that replaces the prior
 * "name + email + Sign Out pill" trio with a single avatar pill that
 * opens a dropdown. Menu content:
 *
 *   [avatar] Display name
 *            email@ojb.com
 *   ─────────────────────────
 *   AI spend today        $0.12 / $10.00
 *   Month-to-date         $4.80
 *   ─────────────────────────
 *   Style profile         (view / reset)
 *   ─────────────────────────
 *   Sign out
 *
 * Keeps the avatar pill on-brand (pine initials on cloud fill) so it
 * matches the rest of the header's pill-first treatment.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import { api } from '../../lib/apiClient.ts';
import { useAuth } from '../../context/AuthContext.tsx';
import { AttendeeAvatar } from '../editor/AttendeeAvatar.tsx';

interface UsageResponse {
  rateLimitDailyUsd: number;
  mtdSpendUsd: number;
  todaySpendUsd: number;
}

interface StyleProfileResponse {
  notes: string;
  updatedAt: string;
  updateCount: number;
}

export function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  // Lazy-fetch usage only when the menu is opened.
  const usage = useQuery({
    queryKey: ['me', 'usage'],
    queryFn: () => api.get<UsageResponse>('/me/usage'),
    enabled: open,
    staleTime: 30_000,
  });

  // Lazy-fetch style profile only when that sub-panel is opened.
  const styleProfile = useQuery({
    queryKey: ['me', 'style-profile'],
    queryFn: () => api.get<StyleProfileResponse>('/me/style-profile'),
    enabled: styleOpen,
    staleTime: 30_000,
  });

  const resetStyle = useMutation({
    mutationFn: () =>
      api.post<{ ok: true; previousUpdateCount: number }>('/me/style-profile/reset'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'style-profile'] });
    },
  });

  // Click-outside to close (both menu and style sub-panel)
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setStyleOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (!user) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        // Solid pine pill with white text \u2014 mirrors the "active" nav
        // pill treatment so the user affordance reads as an intentional
        // parallel to the current-page indicator on the left side.
        className="flex items-center gap-2.5 rounded-full bg-pine py-1 pl-3.5 pr-1 text-xs font-medium text-cloud transition-all duration-200 hover:-translate-y-px hover:bg-pine-deep"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {/* White circle with pine initials \u2014 reverses the pine fill
            so the user's initials pop against the pill background. */}
        <span className="max-w-[9rem] truncate">{user.displayName}</span>
        {/* Headshot from the OJB roster (or a pine-tinted initials
            circle when the user has no photo on file). AttendeeAvatar
            handles both states + adds the pine-ring outline so the
            avatar reads against the pine pill bg. */}
        <AttendeeAvatar
          name={user.displayName}
          headshotUrl={user.headshotUrl ?? null}
          size={28}
          onDark
        />
      </button>

      {open && (
        // StudioShell card: cloud fill + paper-grain texture + pine
        // border + pine halo shadow + asymmetric corner radii (subtle
        // version of the dashboard hero so this header affordance
        // reads as part of the same visual language). Sub-section
        // dividers are charcoal hairlines (matches the dashboard
        // SectionDivider) instead of the smoke gray they were before.
        //
        // `position: absolute` is set inline (not via the Tailwind
        // `absolute` class alone) because `.paper-grain` declares
        // `position: relative` in globals.css to anchor its `::before`
        // noise overlay, and both rules live in `@layer utilities`
        // with equal specificity — the later-declared `.paper-grain`
        // wins, which would put the popout in normal flow, expanding
        // the header row and pushing the logo + nav down. Inline
        // style beats class-level position. The `::before` pseudo
        // still works because `position: absolute` is also a valid
        // positioning context for absolute children.
        <div
          role="menu"
          className="paper-grain right-0 top-full z-40 mt-2 w-80 overflow-hidden rounded-bl-2xl rounded-br-md rounded-tl-md rounded-tr-2xl border border-pine bg-cloud text-sm motion-safe:animate-fade-in-up"
          style={{
            position: 'absolute',
            boxShadow:
              '0 0 24px rgba(55, 85, 66, 0.18), 0 6px 16px rgba(43, 42, 47, 0.10)',
          }}
        >
          {/* Header: name + email */}
          <div className="border-b border-charcoal/20 px-5 py-4">
            <div className="font-semibold text-charcoal">{user.displayName}</div>
            <div className="mt-0.5 text-[11px] text-charcoal/60">{user.email}</div>
          </div>

          {/* AI spend */}
          <div className="border-b border-charcoal/20 px-5 py-4">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-pine">
              AI Spend
            </div>
            {usage.isLoading ? (
              <div className="text-[11px] italic text-charcoal/50">Loading…</div>
            ) : usage.data ? (
              <dl className="space-y-1.5 text-[12px]">
                <div className="flex items-baseline justify-between">
                  <dt className="text-charcoal/75">Today</dt>
                  <dd className="font-medium tabular-nums text-charcoal">
                    ${usage.data.todaySpendUsd.toFixed(2)}{' '}
                    <span className="text-[10px] text-charcoal/50">
                      / ${usage.data.rateLimitDailyUsd.toFixed(2)} cap
                    </span>
                  </dd>
                </div>
                <div className="flex items-baseline justify-between">
                  <dt className="text-charcoal/75">Month to date</dt>
                  <dd className="font-medium tabular-nums text-charcoal">
                    ${usage.data.mtdSpendUsd.toFixed(2)}
                  </dd>
                </div>
              </dl>
            ) : (
              <div className="text-[11px] italic text-charcoal/50">-</div>
            )}
          </div>

          {/* Style profile */}
          <div className="border-b border-charcoal/20">
            <button
              type="button"
              onClick={() => setStyleOpen((v) => !v)}
              className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-pine/5"
            >
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-pine">
                  Style Profile
                </div>
                <div className="mt-0.5 text-[11px] text-charcoal/60">
                  What Claude has learned about your editing
                </div>
              </div>
              <span aria-hidden className="text-xs text-pine/70">
                {styleOpen ? '▴' : '▾'}
              </span>
            </button>

            {styleOpen && (
              <div className="border-t border-charcoal/10 bg-cloud/60 px-5 py-3">
                {styleProfile.isLoading ? (
                  <div className="text-[11px] italic text-charcoal/50">Loading…</div>
                ) : !styleProfile.data || !styleProfile.data.notes.trim() ? (
                  <div className="text-[11px] italic text-charcoal/60">
                    No notes yet. Claude learns after you finalize a few
                    meetings and it sees how you edit its drafts.
                  </div>
                ) : (
                  <>
                    <div className="mb-2 text-[10px] uppercase tracking-[0.18em] text-charcoal/50">
                      Updated {styleProfile.data.updateCount}× · last on{' '}
                      {new Date(styleProfile.data.updatedAt).toLocaleDateString()}
                    </div>
                    <div className="whitespace-pre-wrap rounded-md border border-charcoal/15 bg-white p-3 text-[12px] leading-relaxed text-charcoal">
                      {styleProfile.data.notes}
                    </div>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => {
                    if (
                      confirm(
                        'Reset your style profile to empty? Claude will start learning fresh on your next finalize.',
                      )
                    ) {
                      resetStyle.mutate();
                    }
                  }}
                  disabled={resetStyle.isPending}
                  className="mt-2 text-[11px] text-charcoal/60 underline-offset-2 hover:text-pine hover:underline disabled:opacity-50"
                >
                  {resetStyle.isPending ? 'Resetting…' : 'Reset style profile'}
                </button>
              </div>
            )}
          </div>

          {/* Admin portal — only rendered for admins; replaces the
              Admin pill that used to live in the topline nav. Tucking
              it in here keeps the public nav identical for every user
              and treats admin surfaces as an account-scoped affordance. */}
          {user.isAdmin && (
            <Link
              to="/admin"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 border-b border-charcoal/20 px-5 py-3.5 text-sm text-charcoal no-underline transition-colors hover:bg-pine/5 hover:text-pine"
            >
              <Shield size={14} className="text-pine" aria-hidden />
              <span className="flex-1">Admin portal</span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-pine">
                Admin
              </span>
            </Link>
          )}

          {/* Sign out */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            className="block w-full px-5 py-3.5 text-left text-sm text-charcoal transition-colors hover:bg-pine/5 hover:text-terra-cotta"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// Initials helper removed — AttendeeAvatar handles its own
// initials fallback (with credentials-suffix stripping) so the
// UserMenu doesn't need a separate one.
